import { lazy, Suspense, useMemo } from 'react'
import { ErrorPopupProvider } from '@integration-app/console/components/Error/ErrorPopup'
import { Popup } from '@integration-app/console/components/Popup'
import { Heading } from '@integration-app/console/ui-kit/heading'
import { Text } from '@integration-app/console/ui-kit/text'
import { IntegrationAppClient, IntegrationAppClientProvider } from '@integration-app/react'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'

import { EmbedIsolateWindow } from '../components/EmbedIsolate/EmbedIsolateWindow'
import { EmbedPopupWindow } from '../components/EmbedPopup/EmbedPopupWindow'
import { PopupSizeProvider } from '../components/EmbedPopup/popup-size-context'
import { EmptyPageLoader, PaneLogoLoader } from '../components/Loader'
import { BreadcrumbsProvider } from '../components/Navigation/breadcrumbs-context'
import useAuth, { AuthProvider } from '../contexts/auth'

const RefreshConnectionPage = lazy(() => import('../components/Connections/RefreshConnectionPopup'))
const DataSourceInstancePage = lazy(() => import('../routes/Embed/DataSourceInstances'))
const FieldMappingInstancePage = lazy(() => import('../routes/Embed/FieldMappingInstances'))
const FlowInstancesRoute = lazy(() => import('../routes/Embed/FlowInstances'))
const FlowRunsRoute = lazy(() => import('../routes/Embed/FlowRuns'))
const IntegrationRoutes = lazy(() => import('../routes/Embed/Integrations/Integration'))
const MainRoute = lazy(() => import('../routes/Embed/Main'))
const IsolatedFlowInstanceRoute = lazy(() => import('../routes/IsolatedEmbed/FlowInstances'))
const IsolatedFlowRunPage = lazy(() => import('../routes/IsolatedEmbed/FlowRuns'))

export default function RootRoutes() {
  return (
    <ErrorPopupProvider>
      <AuthProvider>
        <ClientProvider>
          <BrowserRouter>
            <Routes>
              <Route
                element={
                  <PopupSizeProvider>
                    <BreadcrumbsProvider>
                      <EmbedPopupWindow>
                        <Outlet />
                      </EmbedPopupWindow>
                    </BreadcrumbsProvider>
                  </PopupSizeProvider>
                }
              >
                <Route
                  path='/embed/connections/:connectionId/refresh'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <RefreshConnectionPage />
                    </Suspense>
                  }
                />
                <Route
                  path='/embed/data-source-instances/:dataSourceInstanceId/configuration'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <DataSourceInstancePage />
                    </Suspense>
                  }
                />
                <Route
                  path='/embed/field-mapping-instances/:fieldMappingInstanceId/configuration'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <FieldMappingInstancePage />
                    </Suspense>
                  }
                />
                <Route
                  path='/embed/flow-instances/*'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <FlowInstancesRoute />
                    </Suspense>
                  }
                />
                <Route
                  path='/embed/flow-runs/*'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <FlowRunsRoute />
                    </Suspense>
                  }
                />
                <Route
                  path='/embed/integrations/:integrationId/*'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <IntegrationRoutes standalone={true} basePath='/embed/integrations' />
                    </Suspense>
                  }
                />
                <Route
                  path='/embed/main/*'
                  element={
                    <Suspense fallback={<PaneLogoLoader />}>
                      <MainRoute />
                    </Suspense>
                  }
                />
                <Route path='*' element={<NotFoundPage />} />
              </Route>
              <Route
                element={
                  <BreadcrumbsProvider>
                    <EmbedIsolateWindow>
                      <Outlet />
                    </EmbedIsolateWindow>
                  </BreadcrumbsProvider>
                }
              >
                <Route
                  path='/isolated-embed/flow-instances/:flowInstanceId/editor/*'
                  element={
                    <Suspense fallback={<EmptyPageLoader />}>
                      <IsolatedFlowInstanceRoute />
                    </Suspense>
                  }
                />
                <Route
                  path='/isolated-embed/flow-runs/:flowRunId/editor/*'
                  element={
                    <Suspense fallback={<EmptyPageLoader />}>
                      <IsolatedFlowRunPage />
                    </Suspense>
                  }
                />
              </Route>
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </ClientProvider>
      </AuthProvider>
    </ErrorPopupProvider>
  )
}

function NotFoundPage() {
  return (
    <Popup.Content display='flex' flexDirection='column' gap='0' justifyContent='center' alignItems='center'>
      <Heading as='h1' textStyle='2xl' padding='0'>
        Not found
      </Heading>
      <Text textStyle='lg' padding='0'>
        The page you are looking for does not exist.
      </Text>
    </Popup.Content>
  )
}

function ClientProvider({ children }) {
  const { token } = useAuth()

  const client = useMemo(() => {
    if (!token) {
      return null
    }

    return new IntegrationAppClient({
      token,
      apiUri: process.env.NEXT_PUBLIC_ENGINE_URI,
    })
  }, [token])

  return <IntegrationAppClientProvider client={client}>{children}</IntegrationAppClientProvider>
}
